import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { Modal } from '~/components/overlays';
import { Button, Input, PhoneNumberInput, Select } from '~/components/ui';
import { optionalPhoneNumberSchema } from '~/components/ui/inputs/phoneNumber';
import { useFormData } from '~/components/ui/forms/useForm';
import { useCreateTenantUser, useUpdateTenantUser } from '~/api/users';
import { useTradeRoleOptions } from '~/hooks/useTradeRoleOptions';
import { extractGraphQLErrorMessages, type ClientError } from '~/utils';
import { optionalEmployeeIdSchema } from '~/utils/employeeId';
import type { TenantUserRowData } from './DeactivateUserDialog';

const createValidation = z.object({
  email: z.email(),
  firstName: z.string().trim().min(1),
  lastName: z.string().trim().min(1),
  employeeId: optionalEmployeeIdSchema,
  phoneNumber: optionalPhoneNumberSchema,
  // Single role field mirrors the API (CreateTenantUserInput.role). The admin checkbox and the
  // trade-role select both write it, so isValid() can't pass with an unset/invalid role. TRADER is
  // only offered when its feature flag is on (the backend rejects it otherwise).
  role: z.enum(['TENANT_ADMIN', 'BUYER', 'SELLER', 'TRADER']),
});

// Editing covers only UpdateTenantUserInput's fields — role changes go through the existing
// Make admin/Make buyer/etc. row actions, and email can't be changed here (it's the directory
// identity, not a plain detail).
const editValidation = z.object({
  firstName: z.string().trim().min(1),
  lastName: z.string().trim().min(1),
  employeeId: optionalEmployeeIdSchema,
  phoneNumber: optionalPhoneNumberSchema,
});

type Props = {
  isOpen: boolean;
  onClose: () => void;
  user?: TenantUserRowData | null;
};

export function UpsertUserModal({ isOpen, onClose, user }: Props) {
  const { t } = useTranslation('users');
  const isEditMode = Boolean(user);
  const form = useFormData({
    initialData: user
      ? {
          firstName: user.firstName,
          lastName: user.lastName,
          employeeId: user.employeeId ?? '',
          phoneNumber: user.phoneNumber ?? '',
        }
      : {
          email: '',
          firstName: '',
          lastName: '',
          employeeId: '',
          phoneNumber: '',
          role: 'BUYER',
        },
    validation: isEditMode ? editValidation : createValidation,
  });
  const { mutate: createUser, isPending: isCreating } = useCreateTenantUser();
  const { mutate: updateUser, isPending: isUpdating } = useUpdateTenantUser();
  const isPending = isCreating || isUpdating;
  const tradeRoleOptions = useTradeRoleOptions();

  const handleClose = () => {
    form.clear();
    onClose();
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.isValid()) return;

    if (isEditMode && user) {
      const input = editValidation.parse(form.data);
      updateUser(
        { userId: user.id, input },
        {
          onSuccess: () => {
            toast.success(t('users.toast.updated'));
            handleClose();
          },
          onError: (error: ClientError) =>
            toast.error(extractGraphQLErrorMessages(error).join(', ')),
        }
      );
      return;
    }

    // The schema validates email/name and constrains role to TENANT_ADMIN/BUYER/SELLER, so the
    // parsed value is correctly typed — no cast, and no silent fallback to BUYER for a bad value
    // (an unsupported role fails isValid() above and the request is never sent).
    const input = createValidation.parse(form.data);
    createUser(
      { input },
      {
        onSuccess: () => {
          toast.success(t('users.toast.created'));
          handleClose();
        },
        onError: (error: ClientError) =>
          toast.error(extractGraphQLErrorMessages(error).join(', ')),
      }
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="bg-neut-bg-alt rounded-lg p-6 w-[25rem] max-w-full">
        <h2
          id="Modal-title"
          className="text-base font-semibold text-neut-prim mb-4"
        >
          {isEditMode ? t('users.modal.editTitle') : t('users.modal.title')}
        </h2>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          {!isEditMode && (
            <Input
              label={t('users.modal.email')}
              type="email"
              name="email"
              value={form.data.email as string}
              onChange={form.onChange}
              error={form.errors.email?.[0]}
              required
            />
          )}
          <Input
            label={t('users.modal.firstName')}
            name="firstName"
            value={form.data.firstName as string}
            onChange={form.onChange}
            error={form.errors.firstName?.[0]}
            required
          />
          <Input
            label={t('users.modal.lastName')}
            name="lastName"
            value={form.data.lastName as string}
            onChange={form.onChange}
            error={form.errors.lastName?.[0]}
            required
          />
          <Input
            label={t('users.modal.employeeId')}
            name="employeeId"
            value={form.data.employeeId as string}
            onChange={form.onChange}
            error={form.errors.employeeId?.[0]}
          />
          <PhoneNumberInput
            label={t('users.modal.phoneNumber')}
            extensionLabel={t('users.modal.phoneNumberExtension')}
            name="phoneNumber"
            value={form.data.phoneNumber as string}
            onChange={form.onChange}
            error={
              form.errors.phoneNumber
                ? t('users.modal.phoneNumberInvalid')
                : undefined
            }
          />
          {!isEditMode && (
            <>
              <label className="flex items-center gap-2 text-sm text-neut-prim">
                <input
                  type="checkbox"
                  name="tenantAdmin"
                  checked={form.data.role === 'TENANT_ADMIN'}
                  onChange={(event) =>
                    form.onChange(
                      'role',
                      event.target.checked ? 'TENANT_ADMIN' : 'BUYER'
                    )
                  }
                />
                {t('users.modal.adminToggle')}
              </label>
              {form.data.role !== 'TENANT_ADMIN' && (
                <Select
                  label={t('users.modal.tradeRole')}
                  name="role"
                  value={form.data.role as string}
                  onChange={form.onChange}
                  options={tradeRoleOptions}
                />
              )}
            </>
          )}
          <div className="flex justify-end gap-3 mt-2">
            <Button
              size="sm"
              width="auto"
              variant="accent"
              type="button"
              onClick={handleClose}
              disabled={isPending}
            >
              {t('users.modal.cancel')}
            </Button>
            <Button size="sm" width="auto" type="submit" loading={isPending}>
              {isEditMode
                ? t('users.modal.editSubmit')
                : t('users.modal.submit')}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
